import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { Tabs } from '@client/components/_core/navigation/Tabs';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { modal } from '@client/components/_ui/feedback/modal';
import { productVersionFormStore } from '@client/components/products-v2/store';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { useTitleCacheStorage } from '@client/providers/TitleCacheProvider';
import { trpc } from '@client/trpc/client';
import type { ShareableRouteContext } from '@client/types/router';
import { cn } from '@client/utils/styling/cn';
import { MAX_PAGE_SIZE } from '@shared/constants';
import { isTruthy } from '@shared/utils';
import { createFileRoute } from '@tanstack/react-router';
import { Dropdown } from 'antd';
import { Calendar, ChevronDown, CircleAlert, Plus } from 'lucide-react';

function displayVersionName(name: string | null | undefined) {
    const trimmed = name?.trim();

    if (trimmed) {
        return {
            text: trimmed,
            isPlaceholder: false,
        };
    }

    return {
        text: 'Untitled version',
        isPlaceholder: true,
    };
}

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId',
)({
    staticData: {
        metadata: {
            title: 'Product version',
        },
        presentation: 'fullPage',
        ui: {
            className: 'p-0',
        },
    },
    params: {
        parse({ productVersionId }) {
            return {
                productVersionId: BigInt(productVersionId),
            };
        },
    },
    beforeLoad({ context: { trpcUtils }, params }): ShareableRouteContext {
        void trpcUtils.productVersion.getProductVersion.ensureData({ id: params.productVersionId });

        return {
            breadcrumb: {
                async title() {
                    const version = await trpcUtils.productVersion.getProductVersion.ensureData({
                        id: params.productVersionId,
                    });

                    return displayVersionName(version.name).text;
                },
            },
        };
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ProductVersionDetails />
        </PageController>
    );
}

function ProductVersionDetails() {
    const titleCache = useTitleCacheStorage();
    const { orgId, productId, productVersionId } = Route.useParams();
    const { trpcUtils } = useTrpc();
    const { formatDate } = useFormatter();
    const navigate = useNavigate();

    const getProductQuery = trpc.product.getProduct.useQuery({ id: productId });
    const getProductVersionQuery = trpc.productVersion.getProductVersion.useQuery({ id: productVersionId });
    const listProductVersionsQuery = trpc.productVersion.listProductVersions.useQuery({
        where: {
            productId,
        },
        orderBy: {
            version: 'desc',
        },
        limit: MAX_PAGE_SIZE,
    });

    const cloneMutation = trpc.productVersion.cloneLatestProductVersion.useMutation();
    const publishMutation = trpc.productVersion.publishProductVersion.useMutation();
    const revertMutation = trpc.productVersion.revertProductVersion.useMutation();
    const updateMutation = trpc.productVersion.updateProductVersion.useMutation();

    const { formData: productVersion } = productVersionFormStore.useInit({
        entityLabel: 'Product version',
        data: getProductVersionQuery.data,
        isLoading: getProductVersionQuery.isLoading,
        isReadOnly: getProductVersionQuery.data?.status === 'PUBLISHED',

        async onReload() {
            await getProductVersionQuery.refetch();
        },

        async onSave(_, changedValues) {
            try {
                await updateMutation.mutateAsync({
                    id: productVersionId,
                    set: changedValues,
                });

                if ('name' in changedValues) {
                    titleCache.invalidate('breadcrumb:');
                }

                await trpcUtils.productVersion.invalidate();

                return { success: true };
            } catch (error) {
                return { success: false, error };
            }
        },
    });

    if (getProductQuery.isPending || getProductVersionQuery.isPending || listProductVersionsQuery.isPending) {
        return <Loading />;
    }

    const hasDraft = !!listProductVersionsQuery.data?.rows.find((item) => item.status === 'DRAFT');
    const isCurrentLoaded = !!productVersion && productVersion.id === productVersionId;

    /**
     * Publish-readiness, mirroring publishProductVersion's server-side
     * completeness validation. Shown as a hint in the version bar and used by
     * the pre-publish check.
     */
    const missingForPublish = isCurrentLoaded
        ? [
              !productVersion.name && 'Version name is required',
              !productVersion.coverageBasis && 'Coverage basis is required',
              !productVersion.startDate && 'Launch date is required',
              (!productVersion.policyWording || !productVersion.policyWordingFile) &&
                  'Policy wording must have a name and file',
              productVersion.endorsements.some((item) => !item.name || !item.file) &&
                  'All existing endorsements must have a name and file',
          ].filter(isTruthy)
        : [];

    const currentDateInfo = isCurrentLoaded
        ? productVersion.startDate && productVersion.endDate
            ? `${formatDate(productVersion.startDate)} - ${formatDate(productVersion.endDate)}`
            : productVersion.startDate
              ? `${formatDate(productVersion.startDate)} - ongoing`
              : productVersion.status === 'DRAFT'
                ? 'No launch date set'
                : 'No dates set'
        : '';

    const publishCurrentVersion = () => {
        if (missingForPublish.length) {
            modal.warning({
                title: 'Cannot publish version',
                width: 460,
                content: (
                    <>
                        <p className="mb-3">The following required fields must be completed before publishing:</p>
                        <ul className="list-disc pl-5">
                            {missingForPublish.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                    </>
                ),
                okText: 'OK',
            });
            return;
        }

        modal.confirm({
            title: 'Publish version',
            content:
                'This version will go live. Any changes made after publishing will be tracked in the audit log. Are you sure?',
            okText: 'Publish',
            onOk() {
                publishMutation.mutate(
                    { id: productVersionId },
                    {
                        onSuccess() {
                            void trpcUtils.productVersion.invalidate();
                        },
                        onError(error) {
                            errorMessage.show(error);
                        },
                    },
                );
            },
        });
    };

    const revertCurrentVersion = () => {
        if (hasDraft) {
            modal.warning({
                title: 'Cannot revert to draft',
                content: 'Only one draft version can exist at a time.',
                okText: 'OK',
            });

            return;
        }

        modal.confirm({
            title: 'Revert this version to draft?',
            content: 'The version becomes editable again.',
            okText: 'Revert',
            onOk() {
                revertMutation.mutate(
                    { id: productVersionId },
                    {
                        onSuccess() {
                            void trpcUtils.productVersion.invalidate();
                        },
                        onError(error) {
                            errorMessage.show(error);
                        },
                    },
                );
            },
        });
    };

    const createNewVersion = () => {
        cloneMutation.mutate(
            { productId },
            {
                onSuccess({ id }) {
                    void trpcUtils.productVersion.invalidate();

                    navigate({
                        to: '/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId/details',
                        params: {
                            orgId,
                            productId,
                            productVersionId: id,
                        },
                    });
                },
                onError(error) {
                    errorMessage.show(error);
                },
            },
        );
    };

    const { text: currentVersionLabel, isPlaceholder: isCurrentPlaceholder } = displayVersionName(
        isCurrentLoaded ? productVersion.name : undefined,
    );

    return (
        <TRPCErrorBoundary
            errors={[getProductQuery.error, getProductVersionQuery.error, listProductVersionsQuery.error]}
        >
            <div className="px-7 pt-5 pb-4">
                <div className="mb-3.5 flex flex-wrap items-center gap-3">
                    <Dropdown
                        trigger={['click']}
                        onOpenChange={(open) => {
                            // Prefetch the other versions while the menu is open so switching
                            // does not land on a full-page loader (the rail used hover prefetch).
                            if (open) {
                                for (const item of listProductVersionsQuery.data?.rows ?? []) {
                                    if (item.id !== productVersionId) {
                                        void trpcUtils.productVersion.getProductVersion.ensureData({ id: item.id });
                                    }
                                }
                            }
                        }}
                        menu={{
                            items: (listProductVersionsQuery.data?.rows ?? []).map((item) => {
                                const { text: versionLabel, isPlaceholder } = displayVersionName(item.name);
                                const dateRange =
                                    item.startDate && item.endDate
                                        ? `${formatDate(item.startDate)} - ${formatDate(item.endDate)}`
                                        : item.startDate
                                          ? `${formatDate(item.startDate)} - ongoing`
                                          : 'No dates set';

                                return {
                                    key: String(item.id),
                                    label: (
                                        <div className="flex min-w-64 items-center justify-between gap-3 py-0.5">
                                            <div className="min-w-0">
                                                <p
                                                    className={cn(
                                                        'truncate font-medium text-sm',
                                                        isPlaceholder && 'text-muted-foreground italic',
                                                        item.id === productVersionId && 'text-accent-700',
                                                    )}
                                                >
                                                    {versionLabel}
                                                </p>
                                                <p className="text-muted-foreground text-xs">{dateRange}</p>
                                            </div>
                                            <StatusBadge
                                                variant="outlined"
                                                module="productVersion"
                                                status={item.status}
                                            />
                                        </div>
                                    ),
                                    onClick() {
                                        if (item.id !== productVersionId) {
                                            navigate({
                                                to: '/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId/details',
                                                params: {
                                                    orgId,
                                                    productId,
                                                    productVersionId: item.id,
                                                },
                                            });
                                        }
                                    },
                                };
                            }),
                        }}
                    >
                        <button
                            type="button"
                            className="flex cursor-pointer items-center gap-2 rounded-lg border border-separator bg-card px-3 py-1.5 transition-colors hover:bg-muted"
                        >
                            <span
                                className={cn(
                                    'max-w-56 truncate font-semibold text-sm',
                                    isCurrentPlaceholder && 'text-muted-foreground italic',
                                )}
                            >
                                {currentVersionLabel}
                            </span>
                            {isCurrentLoaded && (
                                <StatusBadge
                                    variant="outlined"
                                    module="productVersion"
                                    status={productVersion.status}
                                />
                            )}
                            <ChevronDown size={14} className="text-subtle-foreground" />
                        </button>
                    </Dropdown>

                    <span className="flex items-center gap-1.5 text-subtle-foreground text-xs">
                        <Calendar size={12} />
                        {currentDateInfo}
                    </span>

                    {isCurrentLoaded && productVersion.status === 'DRAFT' && missingForPublish.length > 0 && (
                        <button
                            type="button"
                            className="flex cursor-pointer items-center gap-1.5 text-warning-foreground text-xs hover:underline"
                            onClick={() => {
                                modal.info({
                                    title: 'Needed to publish',
                                    width: 460,
                                    content: (
                                        <ul className="list-disc pl-5">
                                            {missingForPublish.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </ul>
                                    ),
                                    okText: 'OK',
                                });
                            }}
                        >
                            <CircleAlert size={13} />
                            {missingForPublish.length} item{missingForPublish.length === 1 ? '' : 's'} needed to publish
                        </button>
                    )}

                    <div className="flex-1" />

                    {!hasDraft && (
                        <Button
                            size="small"
                            icon={<Plus />}
                            loading={cloneMutation.isPending}
                            onClick={createNewVersion}
                        >
                            New version
                        </Button>
                    )}
                    {isCurrentLoaded && productVersion.status === 'DRAFT' && (
                        <Button
                            type="primary"
                            size="small"
                            loading={publishMutation.isPending}
                            onClick={publishCurrentVersion}
                        >
                            Publish version
                        </Button>
                    )}
                    {isCurrentLoaded && productVersion.status === 'PUBLISHED' && (
                        <Button size="small" loading={revertMutation.isPending} onClick={revertCurrentVersion}>
                            Revert to draft
                        </Button>
                    )}
                </div>

                {isCurrentLoaded && (
                    <Tabs
                        items={[
                            {
                                label: 'Details',
                                to: '/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId/details',
                            },
                            {
                                label: 'Documents',
                                to: '/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId/documents',
                            },
                            {
                                label: 'Fields',
                                to: '/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId/fields',
                            },
                            {
                                label: 'Contact types',
                                to: '/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId/contact-types',
                            },
                        ]}
                    />
                )}
            </div>
        </TRPCErrorBoundary>
    );
}
